import { useState, useEffect, useCallback } from "react";
import type { FilterState } from "@/types/dashboard";

const PRESETS_STORAGE_KEY = "dashboard-filter-presets";

export function useFilterPresets(currentFilters: FilterState, onSelect: (preset: FilterState) => void) {
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [savedPresets, setSavedPresets] = useState<Record<string, FilterState>>({});

  useEffect(() => {
    try {
      const storedPresets = localStorage.getItem(PRESETS_STORAGE_KEY);
      if (storedPresets) {
        setSavedPresets(JSON.parse(storedPresets));
      }
    } catch (e) {
      console.error("Failed to load presets", e);
    }
  }, []);

  const handleSavePreset = useCallback((name: string) => {
    if (name.trim()) {
      const newPresets = { ...savedPresets, [name]: currentFilters };
      setSavedPresets(newPresets);
      localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(newPresets));
      setPresetsOpen(false);
    }
  }, [currentFilters, savedPresets]);

  const handleLoadPreset = useCallback((name: string) => {
    const preset = savedPresets[name];
    if (preset) {
      onSelect(preset);
      setPresetsOpen(false);
    }
  }, [savedPresets, onSelect]);

  const handleDeletePreset = useCallback((name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const newPresets = { ...savedPresets };
    delete newPresets[name];
    setSavedPresets(newPresets);
    localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(newPresets));
  }, [savedPresets]);

  return {
    presetsOpen,
    setPresetsOpen,
    savedPresets,
    handleSavePreset,
    handleLoadPreset,
    handleDeletePreset,
  };
}
